import Immutable from 'seamless-immutable';
import { createSelector } from 'reselect';
// --- external imports ------

import { formatDateForLogbook } from '~/lib/helpers';
import { getNewEmptySalesLogObj } from '~/models/sales-log';
import { getNewEmptyPurchasesLogObj } from '~/models/purchases-log';
import { PRODUCT_TYPES, EXPENSES, LEVELS_TO_UNLOCK_FEATURES } from '~/lib/constants';
import { turnSelectors, employeesSelectors, tradeshowSelectors, levelSelectors } from '~/store/selectors';
// --- internal imports ------

function _getDatesInPeriod(startDate, endDate) {
	let date = startDate, datesInPeriod = [];
	do {
		date = new Date(date);
		datesInPeriod.push(date);
		date = new Date(date.getFullYear(), date.getMonth() + 1, 1);
	} while (date < endDate);
	return datesInPeriod;
};

function getTotalSalesForMonth(state, month) {
	return getTotalSalesForProductInMonth(state, month, PRODUCT_TYPES.TABLE) +
		getTotalSalesForProductInMonth(state, month, PRODUCT_TYPES.CHAIR) +
		getTotalSalesForProductInMonth(state, month, PRODUCT_TYPES.COUCH);
}

function getTotalPurchasesForMonth(state, month) {
	return getTotalPurchasesForProductInMonth(state, month, PRODUCT_TYPES.TABLE) +
		getTotalPurchasesForProductInMonth(state, month, PRODUCT_TYPES.CHAIR) +
		getTotalPurchasesForProductInMonth(state, month, PRODUCT_TYPES.COUCH);
}

function getTotalSalesForProductInMonth(state, month, productType) {
	const salesLogbook = getSystemSalesLogFor(state, month);
	return getTotalFromArrayOrObject(salesLogbook[productType], 'price');
}

function getTotalPurchasesForProductInMonth(state, month, productType) {
	const purchasesLogbook = getSystemPurchasesLogFor(state, month);
	return getTotalFromArrayOrObject(purchasesLogbook[productType], 'cost');
}

function getTotalExpensesForMonth(state, month) {
	const expensesLogbook = getSystemGeneralLogFor(state, month);

	const totalExpenses = Object.values(expensesLogbook)
		.reduce(
			(accExpenses, logEntry) => {
				const isTradeshowExpense = logEntry?.description === 'Tradeshow';
				const isProfitDistribution = logEntry?.description === 'Profit Distribution';
				if(isTradeshowExpense ||  isProfitDistribution) {
					/**
					 * Tradeshows and profit distribution are 
					 * not expenses
					 */
					return accExpenses;
				}

				return accExpenses + logEntry.amount;
			},
			0
		);

	return totalExpenses;
}

function getTotalFromArrayOrObject(target, moneyPropertyName) {
	if (target === undefined || target === null) {
		return 0;
	}

	if (Array.isArray(target)) {
		return getTotalFromArray(target, moneyPropertyName);
	}

	return getTotalFromObject(target, moneyPropertyName);
}

function getTotalFromArray(target, moneyPropertyName) {
	return target.reduce(
		(accumulator, record) => {
			const amount = parseInt(record.amount);
			const cost = parseInt(record[moneyPropertyName]);
			return accumulator + (amount * cost);
		}
		, 0
	);
}

function getTotalFromObject(target, moneyPropertyName) {
	const amount = parseInt(target.amount);
	const cost = parseInt(target[moneyPropertyName]);
	return amount * cost;
}

function _getSystemLogFor(date, state) {
	const dateStr = formatDateForLogbook(date);
	return state.logbook.systemLogsByDate[dateStr];
}

const getLogDatesWithDateStrForReSelect = [
	(state) => (state.logbook.logsByDate),
	(state, date) => (formatDateForLogbook(date))
]; 

export function isLogbookLoaded(state) {
	return state.logbook.isLoaded;
}

export const getSalesLogFor = createSelector(
	getLogDatesWithDateStrForReSelect,
	(logsByDate, dateStr) => {
		const log = logsByDate[dateStr];
		return log && log.sales ? log.sales : new Immutable(getNewEmptySalesLogObj());
	}
);

export const getPurchasesLogFor = createSelector(
	getLogDatesWithDateStrForReSelect,
	(logsByDate, dateStr) => {
		const log = logsByDate[dateStr];
		return log && log.purchases ? log.purchases : new Immutable(getNewEmptyPurchasesLogObj());
	}
);

export const getGeneralLogFor = createSelector(
	getLogDatesWithDateStrForReSelect,
	(logsByDate, dateStr) => {
		const log = logsByDate[dateStr];
		return log && log.general ? log.general : new Immutable({});
	}
);

export function getTotalSalesFor(state, quarter) {
	const { startDate, endDate } = quarter;
	const monthsInPeriod = _getDatesInPeriod(startDate, endDate);
	const totalSales = monthsInPeriod.reduce(
		(accSales, month) => accSales + getTotalSalesForMonth(state, month),
		0
	);

	return totalSales;
}

export function getTotalPurchasesFor(state, quarter) {
	const { startDate, endDate } = quarter;
	const monthsInPeriod = _getDatesInPeriod(startDate, endDate);
	const totalPurchases = monthsInPeriod.reduce(
		(accPurchases, month) => accPurchases + getTotalPurchasesForMonth(state, month),
		0
	);

	return totalPurchases;
}

export function getTotalExpensesFor(state, quarter) {
	const { startDate, endDate } = quarter;
	const monthsInPeriod = _getDatesInPeriod(startDate, endDate);
	const totalExpenses = monthsInPeriod.reduce(
		(accSales, month) => accSales + getTotalExpensesForMonth(state, month),
		0
	);
	const tradeshowExpenses = tradeshowSelectors
		.getTotalPricesForFinishedTradeShowsBetweenDates(state, startDate, endDate);

	return totalExpenses + tradeshowExpenses;
}

export function getSystemSalesLogFor(state, date) {
	const log = _getSystemLogFor(date, state);
	return log && log.sales ? log.sales : new Immutable(getNewEmptySalesLogObj());
}

export function getSystemPurchasesLogFor(state, date) {
	const log = _getSystemLogFor(date, state);
	return log && log.purchases ? log.purchases : new Immutable(getNewEmptyPurchasesLogObj());
}

export function getSystemPurchaseLogFromDateStr(state, logbookDateStr) {
	const log = state.logbook.systemLogsByDate[logbookDateStr];
	return log && log.purchases ? log.purchases : new Immutable(getNewEmptyPurchasesLogObj());
}

export function getSystemGeneralLogFor(state, date) {
	const log = _getSystemLogFor(date, state);
	return log && log.general ? log.general : new Immutable({});
}

export function isLogbookAutomatedForSelectedDate(state, selectedDate) {
	const logbookDate = formatDateForLogbook(selectedDate);
	return state.logbook.isLogbookAutomatedByDate[logbookDate] ?? false;
}

export function shouldAutomaticallyFillLogbook(state) {
	return employeesSelectors.hasBookkeeper(state)
		|| levelSelectors.getLevelNumber(state) < LEVELS_TO_UNLOCK_FEATURES.LOGBOOK_FILL;
}

export function getTotalSalesForLastNMonths(state, n){
    const dates = turnSelectors.getLastNTurnMonths(state, n);
    const revenues = dates.map((date)=>{
        return getTotalSalesForMonth(state, date);
    });
    return revenues;
}
